/**
 * Support sessions — a Doppler operator viewing the app as a customer user
 * (DOP-562). Entered with a one-time code from the internal support console;
 * no customer-facing surface leads here.
 */

import { z } from "zod/v3";
import { apiFetch } from "../utils/apiClient";
import { friendlyApiErrorMessage } from "../utils/apiErrorDetail";

const supportSessionSchema = z.object({
	session_id: z.string(),
	admin_email: z.string(),
	target_user_id: z.string(),
	target_email: z.string(),
	expires_at: z.number(), // epoch ms
	console_url: z.string().nullable().optional(),
});

const exchangeResponseSchema = z.object({
	access_token: z.string(),
	session: supportSessionSchema,
});

export type SupportSession = z.infer<typeof supportSessionSchema>;

/** Trade the one-time code for the session's token. Single use, 60 seconds. */
export async function exchangeSupportCode(
	code: string,
): Promise<z.infer<typeof exchangeResponseSchema>> {
	const response = await apiFetch("/api/v3/support/sessions/exchange", {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ code }),
	});
	if (!response.ok) {
		const error = await response.json().catch(() => ({}));
		throw new Error(friendlyApiErrorMessage(response.status, error.detail));
	}
	return exchangeResponseSchema.parse(await response.json());
}

/** Who is viewing as whom, and until when — as the SERVER has it. */
export async function getSupportSession(): Promise<SupportSession> {
	const response = await apiFetch("/api/v3/support/session", { method: "GET" });
	if (!response.ok) {
		const error = await response.json().catch(() => ({}));
		throw new Error(friendlyApiErrorMessage(response.status, error.detail));
	}
	return supportSessionSchema.parse(await response.json());
}

/** Revoke the session server-side. The token is dead when this returns. */
export async function endSupportSession(): Promise<void> {
	const response = await apiFetch("/api/v3/support/session/end", {
		method: "POST",
	});
	if (!response.ok) {
		const error = await response.json().catch(() => ({}));
		throw new Error(friendlyApiErrorMessage(response.status, error.detail));
	}
}
